import { Component, Input } from "@angular/core";
import { getOverallScoreForUbaAlert } from "@app2/alert/alert-inbox/utils";
import {
    AlertStateChange,
    AnomalyAlertResult,
    InboxAlert,
    QueryTrigger,
    SequenceTriggerAlertResult,
    TriggerType,
    UbaAlertResult
} from "@app2/clients/types/alert-typedefs";
import { StateParams, StateType } from "@app2/fraud/type-defs";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { RouterService } from "@app2/shared/services/router.service";
import { WindowService } from "@app2/shared/services/window.service";
import * as _ from "underscore";

@Component({
    selector: "single-alert-view-header",
    template: `
        <div class="single-alert-view-header">
            <div class="single-alert-details">
                <span class="alert-label">{{ "ALERT" | localize }}</span>
                <ds-button class="hyperlink-button"
                           type="blue-link"
                           (clicked)="copyUrl()"
                           test-id="hyperlink-button">
                    <i class="far fa-link-horizontal"></i>
                </ds-button>
                <ds-button class="hyperlink-button"
                           [disabled]="!previousAlertId"
                           type="blue-link"
                           (clicked)="navigateToAlertId(previousAlertId)"
                           test-id="previous-alert-button">
                    <i class="far fa-chevron-left"></i>
                </ds-button>
                <ds-button class="hyperlink-button"
                           [disabled]="!nextAlertId"
                           type="blue-link"
                           (clicked)="navigateToAlertId(nextAlertId)"
                           test-id="next-alert-button">
                    <i class="far fa-chevron-right"></i>
                </ds-button>
                <span *ngIf="trigger.type !== TriggerType.patternscoutSmartThreshold"
                      class="interval-container"
                      test-id="interval-container">
                    <span *ngIf="trigger.type !== TriggerType.threat">
                        <span>{{ alert.resultTimestamp | shortDateAndTime }}</span>
                        &ndash;
                        <span>{{ alert.resultEndTimestamp | shortDateAndTime }}</span>
                    </span>
                    <span *ngIf="trigger.type === TriggerType.threat">
                        {{ alert.resultTimestamp | shortDateAndTime }}
                    </span>
                </span>
                <ng-container *ngIf="trigger.type !== TriggerType.sequence">
                    <span *ngIf="trigger.type === TriggerType.fraudUba || (alert.primaryTerm && primaryField)"
                          class="primary-term-container"
                          test-id="primary-term-container">
                        <span class="container-label">
                            <ng-container *ngIf="trigger.type !== TriggerType.fraudUba">
                                {{ ("GROUP_BY" | localize | uppercase) }}: {{ primaryField | uppercase }}
                            </ng-container>
                            <ng-container *ngIf="trigger.type === TriggerType.fraudUba">
                                {{ ("USERNAME" | localize | uppercase) }}
                            </ng-container>
                        </span>
                        <a class="link-text no-underline story-view-link"
                           dsRouterLink="."
                           [queryParams]="{ mode: 'alert_story', selected: alert.primaryTerm }"
                           queryParamsHandling="merge"
                           test-id="view-alert-story">
                            {{ alert.primaryTerm }}
                        </a>
                    </span>
                </ng-container>
                <span class="uba-overall-score-container"
                      test-id="uba-overall-score"
                      *ngIf="trigger.type === TriggerType.fraudUba">
                    <span class="container-label">
                        {{ "OVERALL_SCORE" | localize | uppercase }}
                    </span>
                    <span class="uba-overall-score">
                        {{ getOverallScoreForUbaAlert(alert) }}
                    </span>
                </span>
                <span *ngIf="trigger.type === TriggerType.custom"
                      class="count-container"
                      test-id="count-container">
                    <span class="container-label">
                        {{ "COUNT" | localize | uppercase }}
                    </span>
                    <search-link [queryString]="queryString"
                                 [begin]="alert.resultTimestamp"
                                 [end]="alert.resultEndTimestamp"
                                 [defaultText]="false"
                                 [newTab]="true"
                                 [matTooltip]="'INBOX_VIEW_EVENTS' | localize">
                        {{ alert.resultCount }}
                    </search-link>
                </span>
                <span *ngIf="trigger.type === TriggerType.sequence"
                      class="sequence-container"
                      test-id="sequence-container">
                    <span class="container-label">
                        {{ "SEQUENCE_QUERY_ALERT_RESULT_MATCHES" | localize | uppercase }}
                    </span>
                    <ng-container *ngFor="let matchedValue of getMatches(alert); first as isFirst">
                        {{ isFirst ? '' : ',' }}
                        <a class="link-text no-underline story-view-link"
                           dsRouterLink="."
                           [queryParams]="{ mode: 'alert_story', selected: matchedValue }"
                           queryParamsHandling="merge"
                           test-class="view-alert-story">
                            {{ matchedValue }}
                        </a>
                    </ng-container>
                </span>
                <span class="state-container">
                    {{ "STATE" | localize | uppercase }}
                <span class="state-label" test-id="state-label">
                        {{ alert.currentState | localize }}
                    </span>
                </span>
            </div>
            <single-alert-state-changer *ngIf="alert && alert.currentState !== 'ESCALATED'"
                                        [alertId]="alert.id"
                                        [currentState]="alert.currentState"
                                        [resultTimestamp]="alert.resultTimestamp"
                                        [trigger]="trigger"
                                        (onStateChanged)="updateStateParams($event)"></single-alert-state-changer>
        </div>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .single-alert-view-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            line-height: 1rem;
            margin-top: 16px;
        }

        .alert-label {
            font-weight: $font-weight-demibold;
        }

        .hyperlink-button {
            margin-left: 16px;
        }

        .interval-container {
            padding-right: 24px;
            color: $gray-7;
        }

        .count-container, .primary-term-container, .uba-overall-score-container, .sequence-container {
            padding: 0 24px;
            font-weight: $font-weight-demibold;
            color: $gray-8;

            .container-label {
                margin-right: 12px;
            }

            .uba-overall-score {
                color: $gray-7;
            }
        }

        .interval-container, .primary-term-container, .count-container, .uba-overall-score-container, .sequence-container {
            border-right: 1px solid $gray-5;
        }

        .state-container {
            padding: 0 24px;
            color: $gray-8;
            font-weight: $font-weight-demibold;

            .state-label {
                margin-left: 12px;
                font-weight: $font-weight-normal;
            }
        }
    `],
})
export class SingleAlertViewHeaderComponent {
    @Input() trigger: QueryTrigger;
    @Input() alert: InboxAlert | SequenceTriggerAlertResult | AnomalyAlertResult | UbaAlertResult;
    @Input() primaryField: string;
    @Input() queryString: string;
    @Input() previousAlertId: uuid;
    @Input() nextAlertId: uuid;

    TriggerType = TriggerType;
    getOverallScoreForUbaAlert = getOverallScoreForUbaAlert;

    constructor(private savedQueryService: SavedQueryService,
                private notificationsService: NotificationsService,
                private windowService: WindowService,
                private routerService: RouterService) {
    }

    copyUrl(): void {
        this.windowService.copyToClipboard(window.location.href);
        this.notificationsService.showSuccess("COPIED_TO_CLIPBOARD");
    }

    navigateToAlertId(id: uuid): void {
        this.routerService.navigate(["/fraud/inbox"], {
            mode: "alert",
            dropdownFilter: "all",
            sort: "newest_first",
            state: this.alert.currentState,
            selected: id,
        });
    }

    updateStateParams(stateChange: AlertStateChange) {
        this.alert.currentState = stateChange.newState;
        this.routerService.mergeParams<StateParams>({ state: stateChange.newState as StateType });
    }

    getMatches(alert: SequenceTriggerAlertResult): string[] {
        return _.flatten(alert.stepResults?.map(step => step.matchValues.map(mv => mv.value)));
    }
}
